/**
 * 八字命理报告 data 协议封装
 * 把排盘基础数据组装成 schema 结构（engine_meta + data.*），供 /api/agent/stream 使用。
 * 范围：只含核心排盘基础事实；baihua_xiangxi / dongtai_guanxi 置空。
 * 注：后端 AgentController.AgentStreamForm 尚无 data 字段，透传待后续。
 */

import {
  type FourPillars,
  type Stem,
  type Branch,
  type Element,
  STEM_ELEMENT,
  BRANCH_HIDDEN,
} from './baziCalc';
import { getShishen } from './calcTools/bazi/shishenEngine';
import { NAYIN_MAP } from './calcTools/chineseZodiac/wuxingCalc';
import type { BaziReportPerson } from '@/pages/paipan/components/AiBaziReport';
import type { SharedBaziDetailData } from '@/pages/paipan/sharedPaipanData';
import type { DaYun as DisplayDaYun, LiuNian as DisplayLiuNian } from '@/pages/paipan/types';
import type { LiunianItem } from '@/utils/calcTools/bazi/liunianEngine';

// ── engine_meta ──────────────────────────────────────────────
export interface EngineMetaMingliTixi {
  zhu_tixi: string;
  wangshuai_fangfa: string;
  geju_fangfa: string;
  yongshen_fangfa: string[];
  mangduan_fangfa: string[];
  shensha_dingwei: string;
}

export interface EngineMetaCalculation {
  use_true_solar_time: boolean;
  day_boundary_rule: string;
  month_boundary_rule: string;
  year_boundary_rule: string;
  dayun_start_rule: string;
  dayun_direction_rule: string;
}

export interface EngineMeta {
  version: string;
  mingli_tixi: EngineMetaMingliTixi;
  calculation: EngineMetaCalculation;
}

export const ENGINE_META: EngineMeta = {
  version: '1.0',
  mingli_tixi: {
    zhu_tixi: '子平法',
    wangshuai_fangfa: '旺衰扶抑',
    geju_fangfa: '月令取格',
    yongshen_fangfa: ['扶抑', '格局', '调候'],
    mangduan_fangfa: ['十神取象', '宫位取象', '岁运引动', '干支作用'],
    shensha_dingwei: '辅助',
  },
  calculation: {
    use_true_solar_time: true,
    day_boundary_rule: '',
    month_boundary_rule: '节气',
    year_boundary_rule: '立春',
    dayun_start_rule: '',
    dayun_direction_rule: '',
  },
};

// ── BaziData 段类型（各段类型在后续 Task 逐步补齐）──────────────
export interface DongtaiGuanxi {
  yuanju_dayun: unknown[];
  yuanju_liunian: unknown[];
  dayun_liunian: unknown[];
  yuanju_dayun_liunian: unknown[];
}

export interface BaziData {
  jichu_mingpan: JichuMingpan;
  wuxing_wangshuai: WuxingWangshuai;
  rizhu_fenxi: RizhuFenxi;
  shishen_jiegou: ShishenJiegou;
  gongwei: Gongwei;
  bazi_geju: BaziGeju;
  xiyongshen: Xiyongshen;
  tiangan_guanxi: TianGanGuanxi[];
  dizhi_guanxi: DiZhiGuanxi[];
  ganzhi_guanxi_huizong: GanZhiGuanxiHuizong;
  shensha: Shensha;
  dayun: Dayun;
  liunian: Liunian;
  dongtai_guanxi: DongtaiGuanxi;
  baihua_xiangxi: Record<string, never>;
}

export function buildDongtaiGuanxi(): DongtaiGuanxi {
  return { yuanju_dayun: [], yuanju_liunian: [], dayun_liunian: [], yuanju_dayun_liunian: [] };
}

export function buildBaihuaXiangxi(): Record<string, never> {
  return {};
}

export interface BaziReportPayload {
  engine_meta: EngineMeta;
  data: BaziData;
}

// ── PaipanExtras ─────────────────────────────────────────────
export interface PaipanExtras {
  longitude?: number;
  latitude?: number;
  timezone?: number;
  taiYuan?: string;
  mingGong?: string;
  shenGong?: string;
  prevSolarTermName?: string;
  nextSolarTermName?: string;
  yuelingDesc?: string;
}

// 结构类型（structural typing）：只含 toPaipanExtras 用到的字段，
// 避免 utils 依赖 @/pages/* 页面类型（依赖方向保持 utils → 页面，不反向）。
export interface PaipanExtrasSource {
  coordinates?: string;
  taiYuan?: string;
  mingGong?: string;
  shenGong?: string;
  prevSolarTermName?: string;
  nextSolarTermName?: string;
  jieQiDesc?: string;
  renYuanSiLing?: string;
}

/**
 * experience 页面最终用于展示的值。字段名属于页面内部模型，buildBaziReportData
 * 会把它们转换回既有的 stream.data 协议字段，不会把这些页面字段名发给后端。
 */
export interface ExperienceDisplaySnapshot {
  professionalPillars: SharedBaziDetailData['professionalPillars'];
  professionalDaYun: SharedBaziDetailData['professionalDaYun'];
  professionalLiuNian: SharedBaziDetailData['professionalLiuNian'];
  professionalMingGong: SharedBaziDetailData['professionalMingGong'];
  wuxingDetail: SharedBaziDetailData['wuxingDetail'];
  geju: SharedBaziDetailData['geju'];
  xiyong: SharedBaziDetailData['xiyong'];
  shishenDistribution: SharedBaziDetailData['shishenDistribution'];
  shishenStructure: SharedBaziDetailData['shishenStructure'];
  shensha: SharedBaziDetailData['shensha'];
  dayun: SharedBaziDetailData['dayun'];
  currentLiunian: SharedBaziDetailData['currentLiunian'];
  selectedDayun?: SharedBaziDetailData['professionalDaYun'][number];
  qiYunInfo: string;
  jiaoYunInfo: string;
  startAge: number;
  startMonths: number;
  isForward: boolean;
  preciseJiaoYear: number;
  preciseJiaoMonth: number;
}

/** 专业报告只有当前流年有完整详批；非当前年份不能套用另一年的评分和文案。 */
export function resolveExperienceReportLiunian(
  detail: SharedBaziDetailData,
  selectedLiunian?: DisplayLiuNian,
): LiunianItem {
  if (!selectedLiunian) return detail.currentLiunian;
  if (selectedLiunian.year !== detail.currentLiunian.year) return toSelectedLiunianItem(selectedLiunian);
  return {
    ...detail.currentLiunian,
    year: selectedLiunian.year,
    stem: selectedLiunian.tianGan as LiunianItem['stem'],
    branch: selectedLiunian.diZhi as LiunianItem['branch'],
    stemShishen: (selectedLiunian.tianGanShiShen !== undefined ? selectedLiunian.tianGanShiShen : selectedLiunian.tianGanShen) as LiunianItem['stemShishen'],
    branchShishen: (selectedLiunian.diZhiMainShiShen !== undefined ? selectedLiunian.diZhiMainShiShen : selectedLiunian.diZhiShen) as LiunianItem['branchShishen'],
  };
}

function emptyLiunianDomain(): LiunianItem['career'] {
  return { score: 0, level: '平稳', desc: '' };
}

function toSelectedLiunianItem(item: DisplayLiuNian): LiunianItem {
  return {
    year: item.year,
    stem: item.tianGan as LiunianItem['stem'],
    branch: item.diZhi as LiunianItem['branch'],
    stemShishen: (item.tianGanShiShen !== undefined ? item.tianGanShiShen : item.tianGanShen) as LiunianItem['stemShishen'],
    branchShishen: (item.diZhiMainShiShen !== undefined ? item.diZhiMainShiShen : item.diZhiShen) as LiunianItem['branchShishen'],
    stemEl: '' as LiunianItem['stemEl'],
    branchEl: '' as LiunianItem['branchEl'],
    stemColor: '',
    branchColor: '',
    dayunStem: '' as LiunianItem['dayunStem'],
    dayunBranch: '' as LiunianItem['dayunBranch'],
    dayunShishen: '' as LiunianItem['dayunShishen'],
    dayunEl: '' as LiunianItem['dayunEl'],
    dayunColor: '',
    conflicts: [],
    overallScore: 0,
    overallLevel: '平顺',
    annualTheme: '',
    triggerPoint: '',
    riskArea: '',
    proactive: '',
    career: emptyLiunianDomain(),
    wealth: emptyLiunianDomain(),
    love: emptyLiunianDomain(),
    health: emptyLiunianDomain(),
    keyAdvice: [],
    auspicious: [],
    resolve: '',
    isCurrent: true,
    factors: [],
  };
}

function mergeDefined<T extends object>(base: T, override: Partial<T>): T {
  return Object.entries(override).reduce<T>((result, [key, value]) => {
    if (value !== undefined) {
      (result as Record<string, unknown>)[key] = value;
    }
    return result;
  }, { ...base });
}

export function buildExperienceDisplaySnapshot(
  detail: SharedBaziDetailData,
  selectedDayun?: DisplayDaYun,
  selectedLiunian?: DisplayLiuNian,
): ExperienceDisplaySnapshot {
  const professionalDaYun = selectedDayun
    ? detail.professionalDaYun.map(item => ({
       ...(item.startYear === selectedDayun.startYear ? mergeDefined(item, selectedDayun) : item),
     }))
    : detail.professionalDaYun;
  const professionalLiuNian = selectedLiunian
    ? detail.professionalLiuNian.map(item => item.year === selectedLiunian.year
      ? mergeDefined(item, selectedLiunian)
      : item)
    : detail.professionalLiuNian;
  const displayedLiunian = selectedLiunian
    ? professionalLiuNian.find(item => item.year === selectedLiunian.year)
    : undefined;
  return {
    professionalPillars: detail.professionalPillars,
    professionalDaYun,
    professionalLiuNian,
    professionalMingGong: detail.professionalMingGong,
    wuxingDetail: detail.wuxingDetail,
    geju: detail.geju,
    xiyong: detail.xiyong,
    shishenDistribution: detail.shishenDistribution,
    shishenStructure: detail.shishenStructure,
    shensha: detail.shensha,
    dayun: detail.dayun,
     currentLiunian: resolveExperienceReportLiunian(detail, selectedLiunian),
     selectedDayun: selectedDayun
       ? professionalDaYun.find(item => item.startYear === selectedDayun.startYear)
       : undefined,
    qiYunInfo: detail.qiYunInfo,
    jiaoYunInfo: detail.jiaoYunInfo,
    startAge: detail.startAge,
    startMonths: detail.startMonths,
    isForward: detail.isForward,
    preciseJiaoYear: detail.preciseJiaoYear,
    preciseJiaoMonth: detail.preciseJiaoMonth,
  };
}

export function toPaipanExtras(p: PaipanExtrasSource): PaipanExtras {
  const lngMatch = p.coordinates?.match(/东经([\d.]+)/);
  const latMatch = p.coordinates?.match(/北纬([\d.]+)/);
  return {
    longitude: lngMatch ? Number(lngMatch[1]) : undefined,
    latitude: latMatch ? Number(latMatch[1]) : undefined,
    timezone: 8,
    taiYuan: p.taiYuan,
    mingGong: p.mingGong,
    shenGong: p.shenGong,
    prevSolarTermName: p.prevSolarTermName,
    nextSolarTermName: p.nextSolarTermName,
    yuelingDesc: p.renYuanSiLing || p.jieQiDesc,
  };
}

// ── 基础命盘段类型 ───────────────────────────────────────────
export interface CangGan {
  tiangan: string;
  wuxing: string;
  shishen: string;
  canggan_leixing: '本气' | '中气' | '余气';
  weight: number;
}

export interface SizhuPillar {
  tiangan: string;
  dizhi: string;
  tiangan_wuxing: string;
  dizhi_wuxing: string;
  tiangan_yinyang: string;
  dizhi_yinyang: string;
  tiangan_shishen: string;
  canggan: CangGan[];
  nayin: string;
  shier_changsheng: string;
  kongwang: boolean;
}

export interface ChushengXinxi {
  solar_datetime: string;
  true_solar_datetime: string;
  lunar_date: string;
  timezone: number;
  longitude: number;
  latitude: number;
}

export interface Jieqi {
  qian_jieqi: string;
  hou_jieqi: string;
  qian_jieqi_datetime: string;
  hou_jieqi_datetime: string;
  yueling_yiju: string;
}

export interface JichuMingpan {
  chusheng_xinxi: ChushengXinxi;
  jieqi: Jieqi;
  sizhu_bazi: { nianzhu: SizhuPillar; yuezhu: SizhuPillar; rizhu: SizhuPillar; shizhu: SizhuPillar };
  rizhu: { tiangan: string; wuxing: string; yinyang: string };
  taiyuan: { ganzhi: string; nayin: string };
  minggong: { ganzhi: string; wuxing: string };
  shengong: { ganzhi: string; wuxing: string };
  kongwang: { xun: string; dizhi: string[] };
}

// ── 五行旺衰段类型 ───────────────────────────────────────────
export interface WuxingScoreEntry {
  score: number;
  level: string;
  laiyuan_fenjie: {
    tiangan: number;
    dizhi: number;
    canggan: number;
    yueling: number;
    tonggen: number;
    hehua: number;
  };
}

export interface WuxingWangshuai {
  mu: WuxingScoreEntry;
  huo: WuxingScoreEntry;
  tu: WuxingScoreEntry;
  jin: WuxingScoreEntry;
  shui: WuxingScoreEntry;
  yueling_qishi: {
    yuezhi: string;
    jijie: string;
    dangling_wuxing: string;
    siling_wuxing: string[];
    xiuling_wuxing: string[];
    qiuling_wuxing: string[];
    wuxing_yingxiang: Record<string, number>;
  };
  wuxing_zongjie: {
    zuiwang: string;
    zuiruo: string;
    liutong: string;
    hanre: string;
    zaoshi: string;
    pingheng_level: string;
  };
}

// ── 日主分析段类型 ───────────────────────────────────────────
export interface RizhuFenxi {
  tiangan: string;
  wuxing: string;
  strength_score: number;
  strength_level: string;
  deling: { value: boolean; score: number; yiju: string[] };
  dedi: { value: boolean; score: number; yiju: string[] };
  deshi: { value: boolean; score: number; yiju: string[] };
  tonggen: Array<{ dizhi: string; zhuwei: string; genqi_leixing: string; strength: string }>;
  shengfu_liliang: { yin_xing: number; bi_jie: number; total: number };
  xiehao_kezhi_liliang: { shi_shang: number; cai_xing: number; guan_sha: number; total: number };
  qiangruo_yiju: string[];
  zonghe_jielun: string;
}

// ── 十神结构段类型 ───────────────────────────────────────────
export interface ShishenJiegou {
  shuliang: {
    bijian: number; jiecai: number; shishen: number; shangguan: number;
    piancai: number; zhengcai: number; qisha: number; zhengguan: number;
    pianyin: number; zhengyin: number;
  };
  fenbu: Array<{
    shishen: string; zhuwei: string; weizhi: string; ganzhi: string;
    tougan: boolean; tonggen: boolean; strength: string;
  }>;
  shishen_liliang: Record<
    string,
    { strength: string; tonggen: boolean; tougan: boolean }
  >;
  shishen_zuhe: string[];
  shishen_liutong: string[];
  shishen_chongtu: string[];
  zhuyao_shishen: string[];
  que_shi_shishen: string[];
}

const SHISHEN_COUNT_KEYS: Record<string, keyof ShishenJiegou['shuliang']> = {
  比肩: 'bijian', 劫财: 'jiecai', 食神: 'shishen', 伤官: 'shangguan',
  偏财: 'piancai', 正财: 'zhengcai', 七杀: 'qisha', 正官: 'zhengguan',
  偏印: 'pianyin', 正印: 'zhengyin',
};

export function buildShishenJiegou(detail: SharedBaziDetailData): ShishenJiegou {
  const shuliang = {
    bijian: 0, jiecai: 0, shishen: 0, shangguan: 0,
    piancai: 0, zhengcai: 0, qisha: 0, zhengguan: 0, pianyin: 0, zhengyin: 0,
  };
  for (const item of detail.shishenDistribution) {
    const key = SHISHEN_COUNT_KEYS[item.name];
    if (key) shuliang[key] = item.count;
  }
  const fenbu = detail.professionalPillars
    .filter(p => p.label !== '胎柱' && p.label !== '胎元')
    .map(p => ({
      shishen: p.mainStar || '',
      zhuwei: p.label,
      weizhi: '天干',
      ganzhi: `${p.tianGan}${p.diZhi}`,
      tougan: true,
      tonggen: false,
      strength: '',
    }));
  return {
    shuliang,
    fenbu,
    shishen_liliang: {
      bi_jie: { strength: '', tonggen: false, tougan: false },
      shi_shang: { strength: '', tonggen: false, tougan: false },
      cai_xing: { strength: '', tonggen: false, tougan: false },
      guan_sha: { strength: '', tonggen: false, tougan: false },
      yin_xing: { strength: '', tonggen: false, tougan: false },
    },
    shishen_zuhe: [],
    shishen_liutong: [],
    shishen_chongtu: [],
    zhuyao_shishen: [],
    que_shi_shishen: [],
  };
}

// ── 宫位段类型 ───────────────────────────────────────────────
export interface Gongwei {
  nianzhu: { lingyu: string[]; status: string; shishen: string[]; ganzhi_guanxi: string[] };
  yuezhu: { lingyu: string[]; status: string; shishen: string[]; ganzhi_guanxi: string[] };
  rizhu: { lingyu: string[]; status: string; shishen: string[]; ganzhi_guanxi: string[] };
  shizhu: { lingyu: string[]; status: string; shishen: string[]; ganzhi_guanxi: string[] };
  fuqigong: { dizhi: string; shishen: string[]; status: string; ganzhi_guanxi: string[] };
}

export function buildGongwei(detail: SharedBaziDetailData): Gongwei {
  const fp = detail.fourPillars;
  const pillars = detail.professionalPillars.filter(p => p.label !== '胎柱' && p.label !== '胎元');
  const pillarShishen = (label: string) =>
    (pillars.find(p => p.label === label)?.cangGan || []).map(s => s.replace(/\d+$/, ''));
  const dayPillar = pillars.find(p => p.label === '日柱');
  return {
    nianzhu: { lingyu: ['祖上', '早年', '外部环境'], status: '', shishen: pillarShishen('年柱'), ganzhi_guanxi: [] },
    yuezhu: { lingyu: ['父母', '兄弟姐妹', '成长环境', '事业基础'], status: '', shishen: pillarShishen('月柱'), ganzhi_guanxi: [] },
    rizhu: { lingyu: ['自身', '婚姻', '配偶'], status: '', shishen: pillarShishen('日柱'), ganzhi_guanxi: [] },
    shizhu: { lingyu: ['子女', '成果', '晚景'], status: '', shishen: pillarShishen('时柱'), ganzhi_guanxi: [] },
    fuqigong: {
      dizhi: fp.day.branch,
      shishen: (dayPillar?.cangGan || []).map(s => s.replace(/\d+$/, '')),
      status: '',
      ganzhi_guanxi: [],
    },
  };
}

// ── 格局段类型 ───────────────────────────────────────────────
export interface BaziGeju {
  name: string;
  strength: string;
  confidence: number;
  quge_yiju: { yueling: string; yueling_canggan: string; tougan: string; tonggen: boolean; qita_yiju: string[] };
  chengge: { formed: boolean; yiju: string[] };
  poge: { broken: boolean; yinsu: string[] };
  jiuying: { exists: boolean; yinsu: string[] };
  geju_cengci: string;
  beixuan_geju: Array<{ name: string; confidence: number; yiju: string[] }>;
}

export function buildBaziGeju(detail: SharedBaziDetailData): BaziGeju {
  const geju = detail.geju;
  return {
    name: geju.name,
    strength: geju.level || '',
    confidence: geju.gejuPurity / 100,
    quge_yiju: { yueling: '', yueling_canggan: '', tougan: '', tonggen: true, qita_yiju: [] },
    chengge: { formed: false, yiju: [] },
    poge: { broken: false, yinsu: [] },
    jiuying: { exists: false, yinsu: [] },
    geju_cengci: '',
    beixuan_geju: [],
  };
}

// ── 喜用神段类型 ─────────────────────────────────────────────
export interface Xiyongshen {
  yongshen: string[];
  xishen: string[];
  zhongxing: string[];
  jishen: string[];
  choushen: string[];
  xianshen: string[];
  quyong_fangfa: string[];
  quyong_yiju: Record<string, string>;
  tiaohou: {
    enabled: boolean; yongshen: string[]; xishen: string[]; jishen: string[]; yiju: string;
  };
  yongshen_zhuangtai: {
    yuanju_cunzai: boolean; tougan: boolean; tonggen: boolean; strength: string;
    shousun: boolean; shousun_yuanyin: string[];
  };
}

export function buildXiyongshen(detail: SharedBaziDetailData): Xiyongshen {
  const xiyong = detail.xiyong;
  return {
    yongshen: xiyong.xi.map(i => i.element),
    xishen: xiyong.xi.map(i => i.element),
    zhongxing: [],
    jishen: xiyong.ji.map(i => i.element),
    choushen: [],
    xianshen: [],
    quyong_fangfa: xiyong.approaches?.map(a => a.label) ?? [],
    quyong_yiju: {},
    tiaohou: { enabled: false, yongshen: [], xishen: [], jishen: [], yiju: '' },
    yongshen_zhuangtai: { yuanju_cunzai: false, tougan: false, tonggen: false, strength: '', shousun: false, shousun_yuanyin: [] },
  };
}

// ── 干支关系规则（与 clashEngine 同源，避免改既有引擎）──────
const BRANCH_CHONG_MAP: Record<string, string> = {
  子: '午', 午: '子', 丑: '未', 未: '丑', 寅: '申', 申: '寅',
  卯: '酉', 酉: '卯', 辰: '戌', 戌: '辰', 巳: '亥', 亥: '巳',
};
const SAN_HE_GROUPS: string[][] = [
  ['申', '子', '辰'], ['亥', '卯', '未'], ['寅', '午', '戌'], ['巳', '酉', '丑'],
];
const LIU_HE_MAP: Record<string, string> = {
  子: '丑', 丑: '子', 寅: '亥', 亥: '寅', 卯: '戌', 戌: '卯',
  辰: '酉', 酉: '辰', 巳: '申', 申: '巳', 午: '未', 未: '午',
};
const SAN_XING_GROUPS: string[][] = [['寅', '巳', '申'], ['丑', '戌', '未'], ['子', '卯']];
const SELF_XING: string[] = ['辰', '午', '酉', '亥'];
const HAI_MAP: Record<string, string> = {
  子: '未', 未: '子', 丑: '午', 午: '丑', 寅: '巳', 巳: '寅',
  卯: '辰', 辰: '卯', 申: '亥', 亥: '申', 酉: '戌', 戌: '酉',
};
const STEM_HE_MAP: Record<string, string> = {
  甲: '己', 己: '甲', 乙: '庚', 庚: '乙', 丙: '辛', 辛: '丙',
  丁: '壬', 壬: '丁', 戊: '癸', 癸: '戊',
};
const ZHUWEI: [string, string][] = [
  ['nianzhu', '年柱'], ['yuezhu', '月柱'], ['rizhu', '日柱'], ['shizhu', '时柱'],
];

export interface TianGanGuanxi {
  type: string;
  chengyuan: string[];
  laiyuan: string[];
  formed: boolean;
  strength: string;
  yingxiang_shishen: string[];
  yingxiang_gongwei: string[];
  jieguo: string;
}

export interface DiZhiGuanxi {
  type: string;
  chengyuan: string[];
  laiyuan: string[];
  formed: boolean;
  strength: string;
  yingxiang_zhuwei: string[];
  yingxiang_gongwei: string[];
  yingxiang_shishen: string[];
  jieguo: string;
}

export interface GanZhiGuanxiHuizong {
  he: string[];
  chong: string[];
  xing: string[];
  hai: string[];
  po: string[];
  liuhe: string[];
  sanhe: string[];
  sanhui: string[];
  banhe: string[];
  zixing: string[];
  fuyin: string[];
  fanyin: string[];
}

function pairBranches(fp: FourPillars): Array<{ a: string; b: string; la: string; lb: string }> {
  const keys = ['year', 'month', 'day', 'hour'] as const;
  const out: Array<{ a: string; b: string; la: string; lb: string }> = [];
  for (let i = 0; i < keys.length; i++) {
    for (let j = i + 1; j < keys.length; j++) {
      out.push({
        a: fp[keys[i]].branch,
        b: fp[keys[j]].branch,
        la: ZHUWEI[i][1],
        lb: ZHUWEI[j][1],
      });
    }
  }
  return out;
}

export function buildDizhiGuanxi(fourPillars: FourPillars): DiZhiGuanxi[] {
  const result: DiZhiGuanxi[] = [];
  for (const { a, b, la, lb } of pairBranches(fourPillars)) {
    if (BRANCH_CHONG_MAP[a] === b) {
      result.push({ type: '冲', chengyuan: [a, b], laiyuan: [la, lb], formed: true, strength: '', yingxiang_zhuwei: [la, lb], yingxiang_gongwei: [], yingxiang_shishen: [], jieguo: '' });
    }
    if (LIU_HE_MAP[a] === b) {
      result.push({ type: '六合', chengyuan: [a, b], laiyuan: [la, lb], formed: true, strength: '', yingxiang_zhuwei: [la, lb], yingxiang_gongwei: [], yingxiang_shishen: [], jieguo: '' });
    }
    if (HAI_MAP[a] === b) {
      result.push({ type: '害', chengyuan: [a, b], laiyuan: [la, lb], formed: true, strength: '', yingxiang_zhuwei: [la, lb], yingxiang_gongwei: [], yingxiang_shishen: [], jieguo: '' });
    }
    for (const group of SAN_HE_GROUPS) {
      if (a !== b && group.includes(a) && group.includes(b)) {
        result.push({ type: '三合', chengyuan: [a, b], laiyuan: [la, lb], formed: true, strength: '', yingxiang_zhuwei: [la, lb], yingxiang_gongwei: [], yingxiang_shishen: [], jieguo: '' });
      }
    }
    for (const group of SAN_XING_GROUPS) {
      if (a !== b && group.includes(a) && group.includes(b)) {
        result.push({ type: '刑', chengyuan: [a, b], laiyuan: [la, lb], formed: true, strength: '', yingxiang_zhuwei: [la, lb], yingxiang_gongwei: [], yingxiang_shishen: [], jieguo: '' });
      }
    }
    if (a === b && SELF_XING.includes(a)) {
      result.push({ type: '自刑', chengyuan: [a, b], laiyuan: [la, lb], formed: true, strength: '', yingxiang_zhuwei: [la, lb], yingxiang_gongwei: [], yingxiang_shishen: [], jieguo: '' });
    }
  }
  return result;
}

export function buildTianganGuanxi(fourPillars: FourPillars): TianGanGuanxi[] {
  const result: TianGanGuanxi[] = [];
  const keys = ['year', 'month', 'day', 'hour'] as const;
  for (let i = 0; i < keys.length; i++) {
    for (let j = i + 1; j < keys.length; j++) {
      const a = fourPillars[keys[i]].stem;
      const b = fourPillars[keys[j]].stem;
      if (STEM_HE_MAP[a] === b) {
        result.push({ type: '合', chengyuan: [a, b], laiyuan: [ZHUWEI[i][1], ZHUWEI[j][1]], formed: true, strength: '', yingxiang_shishen: [], yingxiang_gongwei: [], jieguo: '' });
      }
    }
  }
  return result;
}

export function buildGanZhiHuizong(
  tiangan: TianGanGuanxi[],
  dizhi: DiZhiGuanxi[],
): GanZhiGuanxiHuizong {
  const empty = (): GanZhiGuanxiHuizong => ({
    he: [], chong: [], xing: [], hai: [], po: [], liuhe: [],
    sanhe: [], sanhui: [], banhe: [], zixing: [], fuyin: [], fanyin: [],
  });
  const hz = empty();
  for (const r of tiangan) {
    if (r.type === '合') hz.he.push(r.chengyuan.join(''));
  }
  for (const r of dizhi) {
    const key = r.chengyuan.join('');
    if (r.type === '冲') hz.chong.push(key);
    else if (r.type === '六合') hz.liuhe.push(key);
    else if (r.type === '三合') hz.sanhe.push(key);
    else if (r.type === '刑') hz.xing.push(key);
    else if (r.type === '害') hz.hai.push(key);
    else if (r.type === '自刑') hz.zixing.push(key);
  }
  return hz;
}

export function calcOriginalGanzhiRelations(fourPillars: FourPillars): {
  tiangan: TianGanGuanxi[];
  dizhi: DiZhiGuanxi[];
  huizong: GanZhiGuanxiHuizong;
} {
  const tiangan = buildTianganGuanxi(fourPillars);
  const dizhi = buildDizhiGuanxi(fourPillars);
  return { tiangan, dizhi, huizong: buildGanZhiHuizong(tiangan, dizhi) };
}

// ── 神煞段类型 ───────────────────────────────────────────────
export interface ShenshaXiangmu {
  name: string;
  leibie: string;
  zhuwei: string;
  ganzhi: string;
  chufa_yiju: string;
  lingyu: string[];
  weight: string;
}

export interface Shensha {
  xiangmu: ShenshaXiangmu[];
  fenlei_huizong: Record<string, string[]>;
  shensha_yuanze: { weight: string; ke_duli_duanji: boolean; shuoming: string };
}

export function buildShensha(detail: SharedBaziDetailData): Shensha {
  const xiangmu: ShenshaXiangmu[] = detail.shensha.map(s => ({
    name: s.name,
    leibie: s.level,
    zhuwei: s.pillars[0] || '',
    ganzhi: '',
    chufa_yiju: '',
    lingyu: [],
    weight: '辅助',
  }));
  const fenlei: Record<string, string[]> = {
    jishen: [], xiongsha: [], guiren: [], taohua: [], qianyi: [],
    xueye: [], shiye: [], caiyun: [], hunyin: [],
  };
  for (const item of xiangmu) {
    if (item.leibie === '大吉' || item.leibie === '吉') fenlei.jishen.push(item.name);
    else if (item.leibie === '注意' || item.leibie === '化解') fenlei.xiongsha.push(item.name);
    // '中' 为中性神煞，不归入 jishen 亦不归入 xiongsha
  }
  return {
    xiangmu,
    fenlei_huizong: fenlei,
    shensha_yuanze: {
      weight: '辅助',
      ke_duli_duanji: false,
      shuoming: '神煞仅用于辅助取象，不替代原局旺衰、格局、喜忌和岁运判断。',
    },
  };
}

// ── 大运流年段类型 ───────────────────────────────────────────
export interface DayunItem {
  index: number;
  ganzhi: string;
  start_year: number;
  end_year: number;
  status: string;
  tiangan_shishen: string;
  dizhi_shishen: string[];
  wuxing: string[];
  xiji_level: string;
  yu_yuanju_guanxi: Array<{ type: string; chengyuan: string[]; strength: string }>;
  yindong_gongwei: string[];
  yindong_shishen: string[];
  dui_geju_yingxiang: string;
  dui_yongshen_yingxiang: string;
  zonghe_qiangdu: number;
  zonghe_jielun: string;
}

export interface Dayun {
  shunni: string;
  qiyun_age: number;
  qiyun_datetime: string;
  current: { index: number; ganzhi: string; start_year: number; end_year: number };
  items: DayunItem[];
}

export interface LiunianItemData {
  year: number;
  ganzhi: string;
  status: string;
  tiangan_shishen: string;
  dizhi_shishen: string[];
  wuxing: string[];
  yu_yuanju_guanxi: Array<{ type: string; chengyuan: string[]; strength: string }>;
  yu_dayun_guanxi: Array<{ type: string; chengyuan: string[]; strength: string }>;
  yindong_gongwei: string[];
  yindong_shishen: string[];
  dui_geju_yingxiang: string;
  dui_yongshen_yingxiang: string;
  xiji_level: string;
  chufa_qiangdu: number;
  zonghe_jielun: string;
}

export interface Liunian {
  current_year: number;
  items: LiunianItemData[];
}

export function buildDayun(detail: SharedBaziDetailData, display?: ExperienceDisplaySnapshot): Dayun {
  const dayun = detail.dayun;
  const professionalDaYun = display?.professionalDaYun;
  const currentYear = detail.currentYear;
  const dayunStatus = (item: { isActive: boolean; startYear: number; endYear: number }): string => {
    if (item.isActive) return 'current';
    return item.endYear < currentYear ? 'past' : 'future';
  };
  const items: DayunItem[] = professionalDaYun
    ? professionalDaYun.map((item, index) => ({
    index,
    ganzhi: `${item.tianGan}${item.diZhi}`,
    start_year: item.startYear,
    end_year: item.endYear,
    status: item.isActive ? 'current' : item.endYear < currentYear ? 'past' : 'future',
    tiangan_shishen: item.tianGanShiShen !== undefined ? item.tianGanShiShen : item.tianGanShen,
    dizhi_shishen: (item.diZhiMainShiShen !== undefined ? item.diZhiMainShiShen : item.diZhiShen) ? [(item.diZhiMainShiShen !== undefined ? item.diZhiMainShiShen : item.diZhiShen)!] : [],
    wuxing: [],
    xiji_level: '',
    // chengyuan 如实保留引擎 DayunConflictItem.target 描述串（如 '年柱地支'、'日柱地支'、
    // '流年巳合大运酉'），非结构化干支成员，勿按成员语义解析
    yu_yuanju_guanxi: [],
    yindong_gongwei: [],
    yindong_shishen: [],
    dui_geju_yingxiang: '',
    dui_yongshen_yingxiang: '',
    zonghe_qiangdu: 0,
    zonghe_jielun: '',
  }))
    : dayun.items.map((item) => ({
    index: item.index,
    ganzhi: `${item.stem}${item.branch}`,
    start_year: item.startYear,
    end_year: item.endYear,
    status: dayunStatus(item),
    tiangan_shishen: item.stemShishen,
    dizhi_shishen: [],
    wuxing: [], xiji_level: '', yu_yuanju_guanxi: (item.conflicts || []).map(c => ({ type: c.type, chengyuan: [c.target], strength: c.effect })),
    yindong_gongwei: [], yindong_shishen: [], dui_geju_yingxiang: '', dui_yongshen_yingxiang: '', zonghe_qiangdu: 0, zonghe_jielun: '',
  }));
  const current = professionalDaYun
    ? (() => {
      const active = display?.selectedDayun || professionalDaYun.find(item => item.isActive) || professionalDaYun[0];
      return active
        ? { index: professionalDaYun.indexOf(active), ganzhi: `${active.tianGan}${active.diZhi}`, start_year: active.startYear, end_year: active.endYear }
        : { index: 0, ganzhi: '', start_year: 0, end_year: 0 };
    })()
    : dayun.currentDayun
    ? { index: dayun.currentDayun.index, ganzhi: `${dayun.currentDayun.stem}${dayun.currentDayun.branch}`, start_year: dayun.currentDayun.startYear, end_year: dayun.currentDayun.endYear }
    : { index: 0, ganzhi: '', start_year: 0, end_year: 0 };
  return {
    shunni: display ? (display.isForward ? '顺' : '逆') : dayun.isForward ? '顺' : '逆',
    qiyun_age: display?.startAge ?? dayun.startAge,
    qiyun_datetime: display?.qiYunInfo || dayun.startDateTime || '',
    current,
    items,
  };
}

export function buildLiunian(detail: SharedBaziDetailData, display?: ExperienceDisplaySnapshot): Liunian {
  const items: LiunianItemData[] = display?.professionalLiuNian
    ? display.professionalLiuNian.map(item => ({
    year: item.year,
    ganzhi: `${item.tianGan}${item.diZhi}`,
    status: display?.currentLiunian.year === item.year
        ? 'current'
        : item.year < detail.currentYear ? 'past' : 'future',
    tiangan_shishen: item.tianGanShiShen !== undefined ? item.tianGanShiShen : item.tianGanShen,
    dizhi_shishen: (item.diZhiMainShiShen !== undefined ? item.diZhiMainShiShen : item.diZhiShen) ? [(item.diZhiMainShiShen !== undefined ? item.diZhiMainShiShen : item.diZhiShen)!] : [],
    wuxing: [],
    yu_yuanju_guanxi: [],
    yu_dayun_guanxi: [], yindong_gongwei: [], yindong_shishen: [], dui_geju_yingxiang: '', dui_yongshen_yingxiang: '', xiji_level: '', chufa_qiangdu: 0, zonghe_jielun: '',
  }))
    : detail.liunianRange.map(item => ({
    year: item.year, ganzhi: `${item.stem}${item.branch}`,
    status: item.isCurrent ? 'current' : item.year < detail.currentYear ? 'past' : 'future',
    tiangan_shishen: item.stemShishen, dizhi_shishen: [],
    wuxing: [],
    yu_yuanju_guanxi: (item.conflicts || []).map(c => ({
      type: c.type,
      chengyuan: [c.target],
      strength: c.effect,
    })),
    yu_dayun_guanxi: [], yindong_gongwei: [], yindong_shishen: [], dui_geju_yingxiang: '', dui_yongshen_yingxiang: '', xiji_level: '', chufa_qiangdu: 0, zonghe_jielun: '',
  }));
  return { current_year: display?.currentLiunian.year ?? detail.currentYear, items };
}

// ── 工具 ─────────────────────────────────────────────────────
const ELEMENT_KEYS: Record<string, 'mu' | 'huo' | 'tu' | 'jin' | 'shui'> = {
  木: 'mu', 火: 'huo', 土: 'tu', 金: 'jin', 水: 'shui',
};

function zeroFenjie() {
  return { tiangan: 0, dizhi: 0, canggan: 0, yueling: 0, tonggen: 0, hehua: 0 };
}

function emptyScoreEntry(): WuxingScoreEntry {
  return { score: 0, level: '', laiyuan_fenjie: zeroFenjie() };
}

export function buildWuxingWangshuai(detail: SharedBaziDetailData): WuxingWangshuai {
  const scores: Record<'mu' | 'huo' | 'tu' | 'jin' | 'shui', WuxingScoreEntry> = {
    mu: emptyScoreEntry(),
    huo: emptyScoreEntry(),
    tu: emptyScoreEntry(),
    jin: emptyScoreEntry(),
    shui: emptyScoreEntry(),
  };
  for (const item of detail.wuxingDetail) {
    const key = ELEMENT_KEYS[item.element];
    if (key) {
      scores[key] = {
        score: item.percent,
        level: item.level,
        laiyuan_fenjie: zeroFenjie(),
      };
    }
  }
  const monthBranch = detail.fourPillars.month.branch;
  const yueling = detail.yueling;
  return {
    mu: scores.mu,
    huo: scores.huo,
    tu: scores.tu,
    jin: scores.jin,
    shui: scores.shui,
    yueling_qishi: {
      yuezhi: monthBranch,
      jijie: '',
      dangling_wuxing: yueling?.activeElement || '',
      siling_wuxing: [],
      xiuling_wuxing: [],
      qiuling_wuxing: [],
      wuxing_yingxiang: {},
    },
    wuxing_zongjie: {
      zuiwang: '',
      zuiruo: '',
      liutong: '',
      hanre: '',
      zaoshi: '',
      pingheng_level: '',
    },
  };
}

/** 问真 7 档 label → payload strength 档位（score 为示意分，供 AI 参考；与服务端 label 同源）。 */
const WANGSHUAI_TO_LEVEL: Record<string, { score: number; level: string }> = {
  极强: { score: 90, level: '极强' },
  身强: { score: 75, level: '身强' },
  偏强: { score: 62, level: '偏强' },
  平和: { score: 50, level: '平和' },
  偏弱: { score: 38, level: '偏弱' },
  身弱: { score: 25, level: '身弱' },
  极弱: { score: 10, level: '极弱' },
};

export function buildRizhuFenxi(detail: SharedBaziDetailData): RizhuFenxi {
  const dayStem = detail.fourPillars.day.stem;
  // 服务端问真 7 档优先（由 detailData.xiyong.wangShuaiLabel 透传）；无 label 时向后兼容 bodyStrength 三档
  const wangShuaiLabel = (detail.xiyong as { wangShuaiLabel?: string } | undefined)?.wangShuaiLabel;
  const wenzhen = wangShuaiLabel ? WANGSHUAI_TO_LEVEL[wangShuaiLabel] : undefined;
  const bodyStrength = detail.xiyong?.bodyStrength ?? 'neutral';
  const strengthScore = wenzhen
    ? wenzhen.score
    : bodyStrength === 'strong' ? 75 : bodyStrength === 'weak' ? 25 : 50;
  const strengthLevel = wenzhen
    ? wenzhen.level
    : bodyStrength === 'strong' ? '偏强' : bodyStrength === 'weak' ? '偏弱' : '中和';
  return {
    tiangan: dayStem,
    wuxing: detail.rizhu.element,
    strength_score: strengthScore,
    strength_level: strengthLevel,
    deling: { value: false, score: 0, yiju: [] },
    dedi: { value: false, score: 0, yiju: [] },
    deshi: { value: false, score: 0, yiju: [] },
    tonggen: [],
    shengfu_liliang: { yin_xing: 0, bi_jie: 0, total: 0 },
    xiehao_kezhi_liliang: { shi_shang: 0, cai_xing: 0, guan_sha: 0, total: 0 },
    qiangruo_yiju: [],
    zonghe_jielun: '',
  };
}

const STEM_YIN_YANG: Record<Stem, string> = {
  甲: '阳', 乙: '阴', 丙: '阳', 丁: '阴', 戊: '阳',
  己: '阴', 庚: '阳', 辛: '阴', 壬: '阳', 癸: '阴',
};

const BRANCH_YIN_YANG: Record<Branch, string> = {
  子: '阳', 丑: '阴', 寅: '阳', 卯: '阴', 辰: '阳', 巳: '阴',
  午: '阳', 未: '阴', 申: '阳', 酉: '阴', 戌: '阳', 亥: '阴',
};

const BRANCH_ELEMENT: Record<Branch, Element> = {
  子: '水', 丑: '土', 寅: '木', 卯: '木', 辰: '土', 巳: '火',
  午: '火', 未: '土', 申: '金', 酉: '金', 戌: '土', 亥: '水',
};

const CHANG_SHENG_NAMES = ['长生', '沐浴', '冠带', '临官', '帝旺', '衰', '病', '死', '墓', '绝', '胎', '养'];
const CHANG_SHENG_MAP: Record<Stem, string[]> = {
  甲: ['亥', '子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌'],
  乙: ['午', '巳', '辰', '卯', '寅', '丑', '子', '亥', '戌', '酉', '申', '未'],
  丙: ['寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥', '子', '丑'],
  丁: ['酉', '申', '未', '午', '巳', '辰', '卯', '寅', '丑', '子', '亥', '戌'],
  戊: ['寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥', '子', '丑'],
  己: ['酉', '申', '未', '午', '巳', '辰', '卯', '寅', '丑', '子', '亥', '戌'],
  庚: ['巳', '午', '未', '申', '酉', '戌', '亥', '子', '丑', '寅', '卯', '辰'],
  辛: ['子', '亥', '戌', '酉', '申', '未', '午', '巳', '辰', '卯', '寅', '丑'],
  壬: ['申', '酉', '戌', '亥', '子', '丑', '寅', '卯', '辰', '巳', '午', '未'],
  癸: ['卯', '寅', '丑', '子', '亥', '戌', '酉', '申', '未', '午', '巳', '辰'],
};

function getCangganType(index: number): CangGan['canggan_leixing'] {
  return index === 0 ? '本气' : index === 1 ? '中气' : '余气';
}

function getNayin(stem: string, branch: string): string {
  return NAYIN_MAP[`${stem}${branch}`] || '';
}

function getChangSheng(stem: Stem, branch: Branch): string {
  const stages = CHANG_SHENG_MAP[stem];
  const idx = stages?.indexOf(branch) ?? -1;
  return idx >= 0 ? CHANG_SHENG_NAMES[idx] : '';
}

function buildSizhuPillar(pillar: { stem: Stem; branch: Branch }, dayStem: Stem): SizhuPillar {
  const hidden = BRANCH_HIDDEN[pillar.branch] || [];
  return {
    tiangan: pillar.stem,
    dizhi: pillar.branch,
    tiangan_wuxing: STEM_ELEMENT[pillar.stem],
    dizhi_wuxing: BRANCH_ELEMENT[pillar.branch],
    tiangan_yinyang: STEM_YIN_YANG[pillar.stem],
    dizhi_yinyang: BRANCH_YIN_YANG[pillar.branch],
    tiangan_shishen: getShishen(dayStem, pillar.stem),
    canggan: hidden.map((h, i) => ({
      tiangan: h.stem,
      wuxing: STEM_ELEMENT[h.stem],
      shishen: getShishen(dayStem, h.stem),
      canggan_leixing: getCangganType(i),
      weight: h.weight,
    })),
    nayin: getNayin(pillar.stem, pillar.branch),
    shier_changsheng: getChangSheng(dayStem, pillar.branch),
    kongwang: false,
  };
}

function parseGanZhiNayin(text?: string): { ganzhi: string; nayin: string } {
  if (!text) return { ganzhi: '', nayin: '' };
  const match = text.match(/^([\u4e00-\u9fff]{2})（([^）]+)）$/);
  if (match) return { ganzhi: match[1], nayin: match[2] };
  return { ganzhi: text.replace(/（.*?）/g, ''), nayin: '' };
}

export function buildChushengXinxi(person: BaziReportPerson, paipan?: PaipanExtras): ChushengXinxi {
  return {
    solar_datetime: person.birthTime || '',
    true_solar_datetime: person.trueSolarTime || '',
    lunar_date: person.lunarDate || '',
    timezone: paipan?.timezone ?? 8,
    longitude: paipan?.longitude ?? 0,
    latitude: paipan?.latitude ?? 0,
  };
}

export function buildJichuMingpan(
  fourPillars: FourPillars,
  person: BaziReportPerson,
  paipan?: PaipanExtras,
): JichuMingpan {
  const dayStem = fourPillars.day.stem;
  const sizhu = {
    nianzhu: buildSizhuPillar(fourPillars.year, dayStem),
    yuezhu: buildSizhuPillar(fourPillars.month, dayStem),
    rizhu: buildSizhuPillar(fourPillars.day, dayStem),
    shizhu: buildSizhuPillar(fourPillars.hour, dayStem),
  };
  return {
    chusheng_xinxi: buildChushengXinxi(person, paipan),
    jieqi: {
      qian_jieqi: paipan?.prevSolarTermName || '',
      hou_jieqi: paipan?.nextSolarTermName || '',
      qian_jieqi_datetime: '',
      hou_jieqi_datetime: '',
      yueling_yiju: paipan?.yuelingDesc || '',
    },
    sizhu_bazi: sizhu,
    rizhu: {
      tiangan: dayStem,
      wuxing: STEM_ELEMENT[dayStem],
      yinyang: STEM_YIN_YANG[dayStem],
    },
    taiyuan: parseGanZhiNayin(paipan?.taiYuan),
    minggong: { ...parseGanZhiNayin(paipan?.mingGong), wuxing: '' },
    shengong: { ...parseGanZhiNayin(paipan?.shenGong), wuxing: '' },
    kongwang: { xun: '', dizhi: [] },
  };
}

export function buildBaziReportData(
  person: BaziReportPerson,
  detail: SharedBaziDetailData,
  paipan?: PaipanExtras,
  display?: ExperienceDisplaySnapshot,
): BaziReportPayload {
  const source = display ? { ...detail, ...display } : detail;
  const fourPillars = source.fourPillars;
  const { tiangan, dizhi, huizong } = calcOriginalGanzhiRelations(fourPillars);
  return {
    engine_meta: ENGINE_META,
    data: {
      jichu_mingpan: buildJichuMingpan(fourPillars, person, paipan),
      wuxing_wangshuai: buildWuxingWangshuai(source),
      rizhu_fenxi: buildRizhuFenxi(source),
      shishen_jiegou: buildShishenJiegou(source),
      gongwei: buildGongwei(source),
      bazi_geju: buildBaziGeju(source),
      xiyongshen: buildXiyongshen(source),
      tiangan_guanxi: tiangan,
      dizhi_guanxi: dizhi,
      ganzhi_guanxi_huizong: huizong,
      shensha: buildShensha(source),
      dayun: buildDayun(source, display),
      liunian: buildLiunian(source, display),
      dongtai_guanxi: buildDongtaiGuanxi(),
      baihua_xiangxi: buildBaihuaXiangxi(),
    },
  };
}
